// The host's censorship rule, in one place: which word lists apply, and how a
// string is tested against them.
//
// Not curse-filter: it rebuilds a RegExp per word (6,165 of them) behind a
// setTimeout(0) every 100 words — ~170ms per submit. Here the word list
// compiles to one regex, memoized per config, and each test is ~0.02ms.
//
// Two matching rules differ from curse-filter, which interpolates words into a
// pattern unescaped and wraps them in \b...\b:
//
// - Every term is escaped, so it matches literally. The dictionary's leetspeak
//   entries ("bi+ch", "sh!+") are spellings people type to evade a filter, not
//   patterns; unescaped, "bi+ch" matched "biiiich" and never itself, and
//   "s.o.b." matched "sxoxbx". Host terms are escaped the same way.
// - \b only works next to an ASCII word character, so it could never match a
//   term starting or ending in punctuation ("c++", "*star") or written in a
//   non-Latin script — every Russian, Ukrainian and Thai entry was inert. Terms
//   are split by whether they start with a word character so only the ones that
//   can use \b get it. Deliberately no lookbehind: Safari added it in 16.4, and
//   an unsupported pattern throws at construction, which would leave every
//   censored input in the room permanently unusable on older iPhones.
const NEVER_MATCHES = () => false;
// Cache key is the whole rule: a room's settings are fixed for its lifetime, so
// this compiles once per room shape and is shared across every input in it.
const matcherCache = new Map();
const escapeLiteral = (word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/**
 * Resolve host mod settings into the word lists that apply.
 *
 * The Twitch terms stay behind censorCustom: they are synced into the host's
 * custom list, and a host who has switched custom words off has switched them off.
 */
export function resolveCensorshipRule(settings) {
    const customWords = [
        ...(settings.censorCustom ? (settings.censorCustomWords ?? []) : []),
        ...(settings.censorCustom && settings.twitchTermsBlocked
            ? (settings.censorTwitchWords ?? [])
            : []),
    ];
    return {
        useDictionary: !!settings.censorCommon,
        customWords,
        rigid: !!settings.censorRigid,
    };
}
/** True when a rule would test anything at all. */
export function isCensorshipActive(rule) {
    return rule.useDictionary || rule.customWords.length > 0;
}
function compile(terms, rigid) {
    // An empty term would become an empty alternative, which matches at every
    // position and would block every input in the room.
    const usable = terms.filter((term) => term.trim().length > 0);
    if (usable.length === 0)
        return NEVER_MATCHES;
    if (rigid) {
        const rigidRegex = new RegExp(`(?:${usable.map(escapeLiteral).join('|')})`, 'i');
        return (text) => rigidRegex.test(text);
    }
    // \b is only satisfiable beside an ASCII word character, so it is applied to
    // the terms that begin with one and withheld from the rest. The trailing
    // (?!\w) is a plain lookahead and needs no such split.
    const wordInitial = [];
    const otherInitial = [];
    for (const term of usable) {
        (/^\w/.test(term) ? wordInitial : otherInitial).push(escapeLiteral(term));
    }
    const branches = [];
    if (wordInitial.length > 0)
        branches.push(`\\b(?:${wordInitial.join('|')})`);
    if (otherInitial.length > 0)
        branches.push(`(?:${otherInitial.join('|')})`);
    const regex = new RegExp(`(?:${branches.join('|')})(?!\\w)`, 'i');
    return (text) => regex.test(text);
}
/**
 * Build the matcher for a rule. Async only because the dictionary is loaded on
 * demand — a room with the common list off never downloads it. Repeat calls for
 * the same rule return the cached matcher without awaiting anything meaningful.
 */
export async function getCensorMatcher(rule) {
    if (!isCensorshipActive(rule))
        return NEVER_MATCHES;
    const cacheKey = JSON.stringify([
        rule.useDictionary,
        rule.rigid,
        rule.customWords,
    ]);
    const cached = matcherCache.get(cacheKey);
    if (cached)
        return cached;
    const terms = [...rule.customWords];
    if (rule.useDictionary) {
        // Extension is required: the CJS build keeps this as a real dynamic import
        // (the only way CJS reaches ESM), and that resolver will not infer one.
        const { CENSORSHIP_DICTIONARY, CENSORSHIP_LANGUAGES } = await import('./censorshipDictionary.js');
        for (const language of CENSORSHIP_LANGUAGES) {
            terms.push(...CENSORSHIP_DICTIONARY[language]);
        }
    }
    const matcher = compile(terms, rule.rigid);
    matcherCache.set(cacheKey, matcher);
    return matcher;
}
/** Convenience wrapper for a one-off check against raw host settings. */
export async function isCensored(text, settings) {
    const matcher = await getCensorMatcher(resolveCensorshipRule(settings));
    return matcher(text);
}
//# sourceMappingURL=censorship.js.map